import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const PENDING = "will-reveal";
const SHOWN = "is-revealed";
const STATE_ATTR = "data-reveal-state";

/**
 * Reveals each page section once as it scrolls into view.
 *
 * Uses a rAF-throttled scroll pass rather than IntersectionObserver, which
 * never fires for a section skipped from below the fold to above it, leaving
 * fast scrolls, hash links and restored positions permanently blank.
 * Sections mount asynchronously on data-driven routes, so new nodes are
 * picked up by a MutationObserver rather than a one-shot scan.
 */
export default function useScrollReveal() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion) return;

    const pending = new Set();
    let frame = null;
    let listening = false;

    const stop = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };

    const tick = () => {
      frame = null;
      const limit = window.innerHeight * 0.9;
      pending.forEach((section) => {
        if (section.getBoundingClientRect().top >= limit) return;
        section.classList.add(SHOWN);
        section.setAttribute(STATE_ATTR, "done");
        pending.delete(section);
      });
      if (!pending.size) stop();
    };

    function onScroll() {
      if (frame === null) frame = requestAnimationFrame(tick);
    }

    const start = () => {
      if (listening || !pending.size) return;
      listening = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
    };

    const register = (node) => {
      if (!(node instanceof HTMLElement)) return;
      const sections =
        node.tagName === "SECTION" ? [node] : node.querySelectorAll("section");
      sections.forEach((section) => {
        if (section.hasAttribute(STATE_ATTR)) return;
        section.setAttribute(STATE_ATTR, "pending");
        section.classList.add(PENDING);
        pending.add(section);
      });
      start();
      tick();
    };

    register(document.body);

    const mutations = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(register));
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      mutations.disconnect();
      stop();
      document.querySelectorAll(`section[${STATE_ATTR}]`).forEach((section) => {
        section.classList.remove(PENDING, SHOWN);
        section.removeAttribute(STATE_ATTR);
      });
    };
  }, [pathname]);
}
